/**
 * PLP (collection page) client-side wiring.
 *
 * Owns:
 *   - Filter form submission via Shopify's Section Rendering API
 *   - Sort changes (desktop dropdown radios + mobile drawer radios)
 *   - Applied-chip removal links
 *   - Skeleton-loading state
 *   - Close-on-outside-click for the desktop sort dropdown
 *
 * Loads only on collection templates (referenced by `sections/collection.liquid`).
 */
(function () {
  const SECTION_ROOT = '[data-plp-section]';
  const SKELETON_MS = 400;

  function plpRoot() {
    return document.querySelector(SECTION_ROOT);
  }

  function showSkeleton(root) {
    root.setAttribute('data-skeleton', '');
  }

  function hideSkeleton(root) {
    root.removeAttribute('data-skeleton');
  }

  function readFilterFormParams(root, form) {
    const params = new URLSearchParams();
    const data = new FormData(form);
    for (const [key, raw] of data.entries()) {
      const value = String(raw).trim();
      if (value === '') continue;
      params.append(key, value);
    }
    // The sort dropdown lives in the toolbar, outside the filter form, so
    // FormData won't pick it up. Read every input[name="sort_by"] inside
    // the section root and let the last non-empty value win (the dropdown
    // and drawer-mode hidden inputs are kept in sync by writeSortValueToForm).
    if (root) {
      const sortInputs = root.querySelectorAll('[data-plp-sort] input[name="sort_by"], [name="sort_by"]');
      let sortValue = null;
      sortInputs.forEach((input) => {
        if (input.value) sortValue = input.value;
      });
      if (sortValue) {
        params.delete('sort_by');
        params.set('sort_by', sortValue);
      }
    }
    return params;
  }

  function buildSectionUrl(baseUrl, params, sectionId) {
    const url = new URL(baseUrl, window.location.origin);
    url.search = '';
    params.forEach((value, key) => url.searchParams.append(key, value));
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function buildVisibleUrl(baseUrl, params) {
    const url = new URL(baseUrl, window.location.origin);
    url.search = '';
    params.forEach((value, key) => url.searchParams.append(key, value));
    return url.toString();
  }

  async function fetchSection(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('PLP section request failed');
    return response.text();
  }

  /* ---------------------------------------------------------------------
     Price filter bounds

     Liquid can't tell the price filter what the cheapest and dearest product
     in the filtered set actually cost: `filter.range_max` is the collection's
     ceiling, there is no `filter.range_min`, and `min_value` / `max_value`
     only report the customer's own selection. So the server renders 0 →
     range_max as the no-JS fallback and we correct the displayed figures here
     by fetching `sections/price-bounds.liquid` once per sort direction.

     Both requests run in parallel with the main section fetch, so under the
     existing SKELETON_MS floor they cost no visible wall-clock.

     Scope: the labels only. The slider geometry still runs off the
     server-rendered `data-range-max` — see sections/price-bounds.liquid.
     --------------------------------------------------------------------- */
  const PRICE_BOUNDS_SECTION = 'price-bounds';
  const PRICE_GROUP = '.filter-group__price';
  // Dropping the price params is what stops the bounds collapsing onto the
  // customer's own selection; dropping `page` keeps them describing the whole
  // filtered set. `sort_by` is replaced outright — the bounds don't care what
  // the customer chose.
  const BOUNDS_OMIT_PARAMS = ['filter.v.price.gte', 'filter.v.price.lte', 'page', 'sort_by'];
  let boundsToken = 0;

  // Search results mix in articles and pages, and `search.results` needs
  // different handling to `collection.products`. Out of scope for now — the
  // search page keeps the server-rendered fallback.
  function wantsPriceBounds(root) {
    if (!root) return false;
    if (root.hasAttribute('data-search-section')) return false;
    return Boolean(root.querySelector(PRICE_GROUP));
  }

  function buildBoundsUrl(baseUrl, params, sortBy) {
    const url = new URL(baseUrl, window.location.origin);
    url.search = '';
    params.forEach((value, key) => {
      if (BOUNDS_OMIT_PARAMS.indexOf(key) !== -1) return;
      url.searchParams.append(key, value);
    });
    url.searchParams.set('sort_by', sortBy);
    url.searchParams.set('section_id', PRICE_BOUNDS_SECTION);
    return url.toString();
  }

  async function fetchBounds(url) {
    try {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = await fetchSection(url);
      const node = wrapper.querySelector('[data-price-bounds]');
      if (!node) return null;
      if (node.dataset.priceBoundsFound !== 'true') return null;
      return {
        min: node.dataset.priceBoundsMinMoney || '',
        max: node.dataset.priceBoundsMaxMoney || '',
      };
    } catch (error) {
      return null;
    }
  }

  async function fetchPriceBounds(baseUrl, params) {
    const [lo, hi] = await Promise.all([
      fetchBounds(buildBoundsUrl(baseUrl, params, 'price-ascending')),
      fetchBounds(buildBoundsUrl(baseUrl, params, 'price-descending')),
    ]);
    // All or nothing: showing a real floor beside a stale ceiling reads worse
    // than leaving the server render alone.
    if (!lo || !hi) return null;
    if (!lo.min || !hi.max) return null;
    return { min: lo.min, max: hi.max };
  }

  function applyPriceBounds(root, bounds) {
    if (!root || !bounds) return;
    root.querySelectorAll(PRICE_GROUP).forEach((group) => {
      const minInput = group.querySelector('[data-price-input="min"]');
      const maxInput = group.querySelector('[data-price-input="max"]');
      const summaryMin = group.querySelector('[data-price-summary-min]');
      const summaryMax = group.querySelector('[data-price-summary-max]');

      if (minInput) minInput.placeholder = bounds.min;
      if (maxInput) maxInput.placeholder = bounds.max;

      // The summary reads the customer's selection where they've made one and
      // the filtered set's bounds where they haven't — so only overwrite the
      // halves they've left alone.
      if (summaryMin && !(minInput && minInput.value)) summaryMin.textContent = bounds.min;
      if (summaryMax && !(maxInput && maxInput.value)) summaryMax.textContent = bounds.max;
    });
  }

  const SWAP_TARGETS = [
    '.plp-section__main',
    '.plp-section__sidebar',
    '.plp-toolbar',
    '.plp-filters-drawer__body',
    // Drawer header / footer hold the active-filter count and "Show N
    // results" label — swap them so they reflect the current result set.
    '#plp-filters .drawer__header',
    '#plp-filters .drawer__footer',
    '.search-section__main',
    '.search-section__sidebar',
    // `.search-section__toolbar` alone matches the Articles toolbar first, so
    // target the products toolbar explicitly — it carries the result count.
    '.search-section__toolbar--products',
    '.search-section__toolbar--articles',
    '.search-section__filters-drawer-body',
  ];

  function swapSection(root, html) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const incoming = wrapper.querySelector(SECTION_ROOT);
    if (!incoming) return;
    // Capture which filter groups are currently expanded. The server-rendered
    // accordion only forces `open` when a group has active values, so
    // deselecting the last value would otherwise collapse the group the
    // customer is still interacting with.
    const openGroups = new Set();
    root.querySelectorAll('[data-filter-group] details.accordion[open]').forEach((details) => {
      const group = details.closest('[data-filter-group]');
      if (group) openGroups.add(group.dataset.filterGroup);
    });
    SWAP_TARGETS.forEach((selector) => {
      const oldNode = root.querySelector(selector);
      const newNode = incoming.querySelector(selector);
      if (oldNode && newNode) oldNode.replaceWith(newNode);
    });
    openGroups.forEach((name) => {
      root.querySelectorAll(`[data-filter-group="${CSS.escape(name)}"] details.accordion`).forEach((details) => {
        details.open = true;
      });
    });
  }

  /* The skeleton grid is far shorter than a full page of results (8 cells vs
     up to 48), so the document collapses on every filter change. A customer
     scrolled past the shortened document's maximum gets clamped to the
     footer, then dropped mid-page when the taller real grid returns -- the
     two-stage jump reported in task 1040. Parking at the top of the results
     before the swap removes the clamp and matches the expectation ("take me
     to the first row"). Only ever scrolls up, so a customer already above
     the results is left where they are. */
  function scrollToResults(root) {
    // Collection and search share this module and this bug; their result
    // columns differ only in class name.
    const main = root.querySelector('.plp-section__main, .search-section__main');
    if (!main) return;
    // Both surfaces pin their filter/sort bar on mobile and drop it into
    // normal flow from 1025px up, so measure rather than assume -- landing
    // the grid under a pinned bar would hide the first row. Search renders
    // two toolbars (products / articles) as `x-show` siblings, so the
    // inactive one contributes its `display: none` height of zero.
    const stickyBarOffset = Array.from(
      root.querySelectorAll('.plp-toolbar, .search-section__toolbar')
    ).reduce(
      (total, bar) =>
        getComputedStyle(bar).position === 'sticky' ? total + bar.offsetHeight : total,
      0
    );
    const headerOffset =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-h')
      ) || 0;
    const target = Math.max(
      0,
      window.scrollY + main.getBoundingClientRect().top - headerOffset - stickyBarOffset
    );
    if (window.scrollY > target) window.scrollTo(0, target);
  }

  // `scrollToTop` follows `pushHistory`: a filter, sort or chip interaction
  // pushes history and wants the results top, while a back/forward restore
  // does neither -- the browser's own scroll restoration owns that case.
  //
  // Deliberately not viewport-gated, unlike the sidebar's scroll container,
  // which is desktop-only because the panel itself is: the skeleton is 8 cells
  // at every width, so the document collapse behind the jump happens on mobile
  // too. The drawer's `html { overflow: hidden }` lock does not block a
  // programmatic scroll, so a mobile filter change repositions the page under
  // the open drawer and the customer lands on the first row when they close
  // it. Task 1040.
  async function applyParams(params, { pushHistory = true, scrollToTop = pushHistory } = {}) {
    const root = plpRoot();
    if (!root) return;
    const baseUrl = root.dataset.collectionUrl || window.location.pathname;
    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    // Before the skeleton shortens the document, not after.
    if (scrollToTop) scrollToResults(root);
    showSkeleton(root);

    // Kicked off alongside the section fetch, applied after the swap — the
    // swap replaces the sidebar, so writing bounds any earlier would land on
    // nodes that are about to be discarded.
    const token = ++boundsToken;
    const boundsPromise = wantsPriceBounds(root)
      ? fetchPriceBounds(baseUrl, params)
      : Promise.resolve(null);

    const sectionUrl = buildSectionUrl(baseUrl, params, sectionId);
    const visibleUrl = buildVisibleUrl(baseUrl, params);
    if (pushHistory) {
      window.history.pushState({}, '', visibleUrl);
    }

    const minWait = new Promise((resolve) => window.setTimeout(resolve, SKELETON_MS));
    let html;
    try {
      [html] = await Promise.all([fetchSection(sectionUrl), minWait]);
    } catch (error) {
      hideSkeleton(root);
      return;
    }
    swapSection(root, html);
    hideSkeleton(root);
    /* Let listeners (e.g. the search tab/count hydrator) re-sync to the new
       result set after the swap. */
    document.dispatchEvent(new CustomEvent('plp:updated'));

    boundsPromise.then((bounds) => {
      // A newer filter interaction has already superseded this one.
      if (token !== boundsToken) return;
      applyPriceBounds(plpRoot(), bounds);
    });
  }

  function onFilterFormSubmit(event) {
    const form = event.target.closest('[data-plp-filter-form]');
    if (!form) return;
    event.preventDefault();
    const root = plpRoot();
    const params = readFilterFormParams(root, form);
    applyParams(params);
  }

  function onFilterFormChange(event) {
    const form = event.target.closest('[data-plp-filter-form]');
    if (!form) return;
    if (event.target.matches('[data-filter-input]')) {
      // Price range needs an explicit Apply to avoid firing on every keystroke.
      return;
    }
    const root = plpRoot();
    const params = readFilterFormParams(root, form);
    applyParams(params);
  }

  function writeSortValueToScope(root, value) {
    if (!root) return;
    // Update every input named sort_by under the section root (dropdown
    // lives in the toolbar outside the form, drawer-mode renders inside
    // the form). Keeps the two in sync regardless of which mode changed.
    root.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      input.value = value;
    });
  }

  function onSortRadioChange(event) {
    const radio = event.target.closest('input[type="radio"][name^="plp-sort-option"]');
    if (!radio || !radio.checked) return;
    const root = plpRoot();
    if (!root) return;
    const form = root.querySelector('[data-plp-filter-form]');
    if (!form) return;
    writeSortValueToScope(root, radio.value);
    // Update the current-label echo next to the trigger text so the change
    // shows immediately, before the section swap finishes. Try each
    // surrounding row's label container in order: desktop bespoke dropdown,
    // mobile drawer (uses the `radio` snippet's own label), then fall back
    // to the raw value if neither structure matches.
    const selectedName =
      radio.closest('.plp-sort__option')?.querySelector('.plp-sort__option-label')?.textContent?.trim() ||
      radio.closest('.radio')?.querySelector('.radio__label')?.textContent?.trim() ||
      radio.value;
    root.querySelectorAll('[data-plp-sort-label]').forEach((node) => {
      node.textContent = selectedName;
    });
    // Close the desktop dropdown and the mobile sort drawer.
    root.querySelectorAll('[data-plp-sort-details]').forEach((d) => { d.open = false; });
    const sortDrawer = document.getElementById('plp-sort');
    if (sortDrawer && sortDrawer.open) sortDrawer.close();
    const params = readFilterFormParams(root, form);
    applyParams(params);
  }

  function onDetailsOutsideClick(event) {
    document.querySelectorAll('[data-plp-sort-details][open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  }

  function onEscape(event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-plp-sort-details][open]').forEach((d) => { d.open = false; });
  }

  function onChipRemoveClick(event) {
    const link = event.target.closest('[data-plp-remove], [data-plp-clear]');
    if (!link) return;
    event.preventDefault();
    const url = new URL(link.href, window.location.origin);
    const params = new URLSearchParams(url.search);
    applyParams(params);
  }

  function onFiltersApplyClick(event) {
    // Filter changes apply live via the form `change` handler, so the
    // drawer's primary action only needs to dismiss the drawer.
    const button = event.target.closest('[data-plp-filters-apply] .drawer-footer__primary');
    if (!button) return;
    event.preventDefault();
    const dialog = document.getElementById('plp-filters');
    if (dialog && dialog.open) dialog.close();
  }

  function onPopState() {
    const params = new URLSearchParams(window.location.search);
    applyParams(params, { pushHistory: false });
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    // Sort radios live in two places: the desktop dropdown (inside the
    // toolbar) and the mobile sort drawer (rendered as a sibling dialog).
    // Route by radio name so both fire the same handler regardless of
    // their parent container.
    if (target && target.matches && target.matches('input[type="radio"][name^="plp-sort-option"]')) {
      onSortRadioChange(event);
      return;
    }
    onFilterFormChange(event);
  });
  document.addEventListener('submit', onFilterFormSubmit);
  document.addEventListener('click', onChipRemoveClick);
  document.addEventListener('click', onFiltersApplyClick);
  document.addEventListener('click', onDetailsOutsideClick);
  document.addEventListener('keydown', onEscape);
  window.addEventListener('popstate', onPopState);

  /* A filtered URL loaded as a fresh document — a shared link, a reload, back
     into a filtered view — never runs applyParams, so it would keep the
     server's 0 → range_max fallback. Same bug, so correct it here too.
     Deliberately skipped on an unfiltered collection: nothing is stale there
     yet, and this would otherwise cost two requests on every PLP view. */
  function hydratePriceBounds() {
    const root = plpRoot();
    if (!wantsPriceBounds(root)) return;
    const params = new URLSearchParams(window.location.search);
    let filtered = false;
    params.forEach((value, key) => {
      if (key.indexOf('filter.') === 0) filtered = true;
    });
    if (!filtered) return;
    const baseUrl = root.dataset.collectionUrl || window.location.pathname;
    const token = ++boundsToken;
    fetchPriceBounds(baseUrl, params).then((bounds) => {
      if (token !== boundsToken) return;
      applyPriceBounds(plpRoot(), bounds);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydratePriceBounds);
  } else {
    hydratePriceBounds();
  }
})();
